import React, { useEffect, useState } from 'react';
import {
  BaseButton,
  Chip,
  Input,
  getBusinessProfile,
  saveBusinessProfile,
  useAuth,
} from '@repo/common';

import PersonalInfoProfileImage from '../PersonalInfo/PersonalInfoProfileImage';
import {
  BUSINESS_PROFILE_STORAGE_KEY,
  BUSINESS_PURPOSES,
  BUSINESS_ROLES,
  BUSINESS_TYPES,
} from '../../../../constants/businessProfile';

const EMPTY_COMPANY_INFO = {
  companyName: '',
  contactName: '',
  registrationNumber: '',
  companyRegistrationNumber: '',
  location: '',
  website: '',
  businessType: '',
  purposes: [],
  roles: [],
};

/**
 * 서버·저장소에서 온 값을 화면이 기대하는 모양으로 맞춘다.
 * 스프레드만 쓰면 아직 입력하지 않은 항목이 `null` 로 덮여 `.trim()` 에서 터진다.
 */
const normalizeCompanyInfo = (raw = {}) => {
  const next = { ...EMPTY_COMPANY_INFO };

  Object.keys(EMPTY_COMPANY_INFO).forEach((key) => {
    const value = raw?.[key];
    if (Array.isArray(EMPTY_COMPANY_INFO[key])) {
      next[key] = Array.isArray(value) ? value : [];
    } else {
      next[key] = value == null ? '' : String(value);
    }
  });

  return next;
};

const readCompanyInfo = () => {
  try {
    const stored =
      sessionStorage.getItem(BUSINESS_PROFILE_STORAGE_KEY) ||
      localStorage.getItem(BUSINESS_PROFILE_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : {};

    return normalizeCompanyInfo(parsed);
  } catch (error) {
    console.error('Failed to restore company info:', error);
    return { ...EMPTY_COMPANY_INFO };
  }
};

const writeCompanyInfo = (companyInfo) => {
  const serialized = JSON.stringify(companyInfo);
  sessionStorage.setItem(BUSINESS_PROFILE_STORAGE_KEY, serialized);
  localStorage.setItem(BUSINESS_PROFILE_STORAGE_KEY, serialized);
};

const CompanyInfoRow = ({ label, value, required = false }) => {
  const displayValue = Array.isArray(value) ? value.join(', ') : value;

  return (
    <div className='flex flex-col gap-1 tabletH:flex-row tabletH:items-start tabletH:gap-2 w-full'>
      <div className='tabletH:w-[160px] shrink-0 text-text-secondary typo-b12Strong tabletH:typo-b14'>
        {label}
        {required && <span className='text-text-accent ml-0.5'>*</span>}
      </div>
      <div
        className={`typo-b14 ${
          displayValue ? 'text-text-primary' : 'text-text-disable'
        }`}
      >
        {displayValue || 'Not added yet'}
      </div>
    </div>
  );
};

const CompanyChipGroup = ({ options, selected, multiple = false, onChange }) => {
  const handleSelect = (option) => {
    if (!multiple) {
      onChange(selected === option ? '' : option);
      return;
    }

    onChange(
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected, option],
    );
  };

  return (
    <div className='flex flex-wrap gap-2 w-full'>
      {options.map((option) => {
        const isSelected = multiple
          ? selected.includes(option)
          : selected === option;

        return (
          <Chip
            key={option}
            type='outline'
            state={isSelected ? 'selected' : 'default'}
            size='m'
            hideIcon
            onClick={() => handleSelect(option)}
            bgclass='bg-grayscale-gray00'
          >
            {option}
          </Chip>
        );
      })}
    </div>
  );
};

const REGISTRATION_ERROR_MESSAGES = {
  INVALID_FORMAT: 'Please enter a valid 10-digit business registration number.',
  NOT_REGISTERED:
    'This business registration number is not registered with the National Tax Service.',
  SUSPENDED: 'This business is currently suspended.',
  CLOSED: 'This business has been closed.',
};

const CompanyInfoForm = ({ value, onChange, registrationError }) => {
  const setField = (field) => (event) =>
    onChange({ ...value, [field]: event.target.value });

  return (
    <div className='flex flex-col gap-8 w-full'>
      <div className='flex flex-col gap-4 w-full'>
        <Input
          label='Company name *'
          placeholder='Company name'
          value={value.companyName}
          onChange={setField('companyName')}
          hideCount
          showStatusIcon={false}
          className='w-full'
        />
        <Input
          label='Contact person name *'
          placeholder='Contact person name'
          value={value.contactName}
          onChange={setField('contactName')}
          hideCount
          showStatusIcon={false}
          className='w-full'
        />
        <Input
          label='Business Registration No. *'
          placeholder='Business Registration No.'
          value={value.registrationNumber}
          onChange={setField('registrationNumber')}
          state={registrationError ? 'failed' : 'default'}
          message={registrationError || undefined}
          hideCount
          showStatusIcon={false}
          className='w-full'
        />
        <Input
          label='Company registration number'
          placeholder='As registered in your country'
          value={value.companyRegistrationNumber}
          onChange={setField('companyRegistrationNumber')}
          hideCount
          showStatusIcon={false}
          className='w-full'
        />
        <Input
          label='Location *'
          placeholder='Location'
          value={value.location}
          onChange={setField('location')}
          hideCount
          showStatusIcon={false}
          className='w-full'
        />
        <Input
          label='Company website'
          placeholder='Company website'
          value={value.website}
          onChange={setField('website')}
          hideCount
          showStatusIcon={false}
          className='w-full'
        />
      </div>

      <div className='flex flex-col gap-4 w-full'>
        <div className='flex flex-col gap-0.5'>
          <h2 className='typo-b14Strong text-text-primary'>Business type</h2>
          <p className='typo-b12 text-text-placeholder'>
            Select the type that best describes your company
          </p>
        </div>
        <CompanyChipGroup
          options={BUSINESS_TYPES}
          selected={value.businessType}
          onChange={(businessType) => onChange({ ...value, businessType })}
        />
      </div>

      <div className='flex flex-col gap-4 w-full'>
        <div className='flex flex-col gap-0.5'>
          <h2 className='typo-b14Strong text-text-primary'>Main purpose</h2>
          <p className='typo-b12 text-text-placeholder'>
            What do you plan to use O!ng for?
          </p>
        </div>
        <CompanyChipGroup
          options={BUSINESS_PURPOSES}
          selected={value.purposes}
          multiple
          onChange={(purposes) => onChange({ ...value, purposes })}
        />
      </div>

      <div className='flex flex-col gap-4 w-full'>
        <div className='flex flex-col gap-0.5'>
          <h2 className='typo-b14Strong text-text-primary'>
            사내에 포함된 Position &amp; Roles
          </h2>
          <p className='typo-b12 text-text-placeholder'>
            Who are you creating content for?
          </p>
        </div>
        <CompanyChipGroup
          options={BUSINESS_ROLES}
          selected={value.roles}
          multiple
          onChange={(roles) => onChange({ ...value, roles })}
        />
      </div>
    </div>
  );
};

const CompanyInfo = () => {
  const { user } = useAuth();
  const [savedInfo, setSavedInfo] = useState(readCompanyInfo);
  const [companyInfo, setCompanyInfo] = useState(readCompanyInfo);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registrationError, setRegistrationError] = useState('');

  useEffect(() => {
    if (!user) return;

    const fallbackContactName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();

    setCompanyInfo((current) => ({
      ...current,
      companyName: current.companyName || user.companyName || '',
      contactName: current.contactName || fallbackContactName,
    }));
    setSavedInfo((current) => ({
      ...current,
      companyName: current.companyName || user.companyName || '',
      contactName: current.contactName || fallbackContactName,
    }));
  }, [user]);

  /*
   * 서버가 원본이다. 브라우저 저장소 값은 첫 렌더를 채우는 캐시일 뿐이라,
   * 서버 응답이 오면 그걸로 덮어쓴다. (기기를 바꿔도 회사 정보가 따라온다)
   */
  useEffect(() => {
    if (!user) return;

    let alive = true;

    getBusinessProfile()
      .then((profile) => {
        if (!alive || !profile?.hasProfile) return;

        const next = normalizeCompanyInfo(profile);
        setCompanyInfo(next);
        setSavedInfo(next);
        writeCompanyInfo(next);
      })
      .catch((error) => {
        // 조회에 실패하면 캐시 값을 그대로 보여준다
        console.error('기업 정보 조회 실패:', error);
      });

    return () => {
      alive = false;
    };
  }, [user]);

  const requiredComplete =
    companyInfo.companyName.trim() &&
    companyInfo.contactName.trim() &&
    companyInfo.registrationNumber.trim() &&
    companyInfo.location.trim();

  const handleCancel = () => {
    setCompanyInfo(savedInfo);
    setRegistrationError('');
    setIsEditing(false);
    window.scrollTo({ top: 0 });
  };

  const handleSave = async () => {
    if (!requiredComplete || saving) return;

    const nextInfo = {
      ...companyInfo,
      companyName: companyInfo.companyName.trim(),
      contactName: companyInfo.contactName.trim(),
      registrationNumber: companyInfo.registrationNumber.trim(),
      companyRegistrationNumber: companyInfo.companyRegistrationNumber.trim(),
      location: companyInfo.location.trim(),
      website: companyInfo.website.trim(),
    };

    setSaving(true);
    try {
      await saveBusinessProfile(nextInfo);
    } catch (error) {
      // 한국 기업의 사업자등록번호가 국세청 조회에서 거절되면 편집 화면에 머문다
      const status = error?.response?.data?.data?.status;
      if (REGISTRATION_ERROR_MESSAGES[status]) {
        setRegistrationError(REGISTRATION_ERROR_MESSAGES[status]);
        return;
      }
      console.error('기업 정보 저장 실패:', error);
    } finally {
      setSaving(false);
    }

    writeCompanyInfo(nextInfo);
    setCompanyInfo(nextInfo);
    setSavedInfo(nextInfo);
    setRegistrationError('');
    setIsEditing(false);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className='flex flex-col w-full tabletH:w-[568px] gap-10 tabletH:gap-6 tabletH:pb-[60px]'>
      <div className='flex justify-between items-start gap-4 w-full'>
        <div className='flex flex-col gap-2'>
          <h1 className='typo-b18Strong tabletH:typo-title1 text-text-primary'>
            Company Info
          </h1>
          <p className='typo-b11 tabletH:typo-b14 text-text-placeholder'>
            Manage the company details used for hiring, partnerships, and
            business services.
          </p>
        </div>
        {!isEditing && (
          <BaseButton
            size='s'
            hideIcon
            width='90px'
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </BaseButton>
        )}
      </div>

      <PersonalInfoProfileImage />

      {isEditing ? (
        <>
          <CompanyInfoForm
            value={companyInfo}
            onChange={(next) => {
              if (next.registrationNumber !== companyInfo.registrationNumber) {
                setRegistrationError('');
              }
              setCompanyInfo(next);
            }}
            registrationError={registrationError}
          />
          <div className='flex justify-end items-center gap-2 w-full'>
            <BaseButton
              type='secondary'
              size='l'
              hideIcon
              onClick={handleCancel}
              bgclass='bg-transform'
            >
              Cancel
            </BaseButton>
            <BaseButton
              size='l'
              hideIcon
              isDisabled={!requiredComplete}
              isLoading={saving}
              onClick={handleSave}
            >
              Save Profile
            </BaseButton>
          </div>
        </>
      ) : (
        <div className='flex flex-col gap-5 tabletH:gap-6 w-full'>
          <CompanyInfoRow
            label='Company name'
            value={companyInfo.companyName}
            required
          />
          <CompanyInfoRow
            label='Contact person name'
            value={companyInfo.contactName}
            required
          />
          <CompanyInfoRow
            label='Business Registration No.'
            value={companyInfo.registrationNumber}
            required
          />
          <CompanyInfoRow
            label='Company registration number'
            value={companyInfo.companyRegistrationNumber}
          />
          <CompanyInfoRow
            label='Location'
            value={companyInfo.location}
            required
          />
          <CompanyInfoRow label='Company website' value={companyInfo.website} />
          <CompanyInfoRow
            label='Business type'
            value={companyInfo.businessType}
          />
          <CompanyInfoRow label='Main purpose' value={companyInfo.purposes} />
          <CompanyInfoRow
            label='Position & Roles'
            value={companyInfo.roles}
          />
        </div>
      )}
    </div>
  );
};

export default CompanyInfo;
